import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatPrice } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Minus, Plus, ImageOff, ArrowLeft, ShoppingCart, Check, FileText, Truck, MessageCircle } from "lucide-react";
import { ProductCard } from "../components/shared";
import { useSettings } from "../context/SettingsContext";
import { Seo, absUrl, SITE_NAME, breadcrumb } from "../lib/seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let active = true;
    setProduct(null); setNotFound(false); setRelated([]); setQty(1);
    api.get(`/products/${slug}`).then(({ data }) => {
      if (!active) return;
      setProduct(data);
      setActiveImg(data.image_url);
      if (data.slug && data.slug !== slug) navigate(`/prodotto/${data.slug}`, { replace: true });
      api.get(`/products/${data.id}/related`).then(({ data: r }) => { if (active) setRelated(r); }).catch(() => {});
    }).catch(() => { if (active) setNotFound(true); });
    return () => { active = false; };
  }, [slug, navigate]);

  if (notFound) return (
    <>
      <Seo title="Prodotto non trovato | Herbalife Rossano" description="Il prodotto cercato non è disponibile nel catalogo Herbalife Rossano." noindex />
      <div className="py-24 text-center text-slate-500">Prodotto non trovato. <Link to="/catalogo" className="text-primary">Torna al catalogo</Link></div>
    </>
  );
  if (!product) return <div className="py-24 text-center text-slate-400">Caricamento...</div>;

  const soldOut = !product.available || (product.stock != null && product.stock <= 0);
  const gallery = [product.image_url, ...(product.images || [])].filter(Boolean);
  const shipCost = settings?.shipping_cost ?? 6;
  const freeThr = settings?.free_shipping_threshold ?? 60;
  const dmin = settings?.delivery_days_min ?? 2;
  const dmax = settings?.delivery_days_max ?? 5;
  const shipInfo = `Spedizione ${formatPrice(shipCost)} (gratuita sopra ${formatPrice(freeThr)}) · consegna in ${dmin}-${dmax} giorni lavorativi.`;
  const canonical = `/prodotto/${product.slug || product.id}`;
  const metaDesc = (product.short_description || product.description || product.name || "")
    .replace(/\s+/g, " ").trim().slice(0, 300);

  const productLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: [fileUrl(product.image_url), ...(product.images || []).map(fileUrl)].filter(Boolean),
    brand: { "@type": "Brand", name: "Herbalife" },
  };
  const fullDesc = (product.description || product.short_description || "").replace(/\s+/g, " ").trim();
  if (fullDesc) productLd.description = fullDesc.slice(0, 5000);
  if (product.sku) productLd.sku = product.sku;
  if (product.category_name) productLd.category = product.category_name;
  if (product.price > 0) productLd.offers = {
    "@type": "Offer",
    price: product.price,
    priceCurrency: "EUR",
    availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
    url: absUrl(canonical),
  };

  const crumb = breadcrumb([
    { name: "Home", path: "/" },
    { name: "Catalogo", path: "/catalogo" },
    ...(product.category_name ? [{ name: product.category_name, path: `/catalogo?category=${product.category_id}` }] : []),
    { name: product.name, path: canonical },
  ]);

  return (
    <div className="mx-auto max-w-6xl px-6 pt-10 pb-28 lg:pb-10">
      <Seo title={`${product.name} | ${SITE_NAME}`} description={metaDesc} canonical={canonical} image={fileUrl(product.image_url)} type="product" jsonLd={[productLd, crumb]} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary mb-6" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Indietro</button>
      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square rounded-3xl bg-emerald-50/60 overflow-hidden flex items-center justify-center">
            {fileUrl(activeImg) ? <img src={fileUrl(activeImg)} alt={product.name} className="w-full h-full object-cover" /> : <ImageOff className="w-16 h-16 text-emerald-200" />}
          </div>
          {gallery.length > 1 && (
            <div className="mt-3 flex gap-2">
              {gallery.map((g, i) => (
                <button key={i} onClick={() => setActiveImg(g)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${activeImg === g ? "border-emerald-500" : "border-transparent"}`}>
                  <img src={fileUrl(g)} alt={`${product.name} - immagine ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          {product.category_name && <Link to={`/catalogo?category=${product.category_id}`} className="text-xs font-semibold uppercase tracking-wider text-emerald-600 hover:underline">{product.category_name}</Link>}
          <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold font-display text-slate-900">{product.name}</h1>
          {product.sku && <p className="mt-1 text-xs text-slate-400">Cod. {product.sku}</p>}
          <div className="mt-4 flex items-baseline gap-3">
            <span className="text-3xl font-extrabold text-slate-900">{formatPrice(product.price)}</span>
            {product.old_price > product.price && <span className="text-lg text-slate-400 line-through">{formatPrice(product.old_price)}</span>}
          </div>
          <div className="mt-3">
            {soldOut ? <span className="inline-block px-3 py-1 rounded-full bg-slate-100 text-slate-500 text-sm font-medium" data-testid="detail-unavailable">Non disponibile</span>
              : <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium"><Check className="w-4 h-4" /> Disponibile</span>}
          </div>
          <p className="mt-5 text-slate-600 leading-relaxed whitespace-pre-line">{product.description || product.short_description}</p>

          {product.usage && (
            <Card className="mt-6 p-5 rounded-2xl bg-emerald-50/50 border-emerald-100" data-testid="product-usage-block">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> Modo di utilizzo</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{product.usage}</p>
            </Card>
          )}

          {product.label_pdf && (
            <a href={product.label_pdf} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 hover:border-emerald-300 hover:text-primary transition-colors" data-testid="product-label-pdf-link">
              <FileText className="w-4 h-4 text-emerald-600" /> Ingredienti e valori nutrizionali (scheda ufficiale PDF)
            </a>
          )}

          <div className="mt-5 rounded-2xl border border-slate-200 p-4 space-y-2 text-sm text-slate-600" data-testid="product-shipping-info">
            <p className="flex items-center gap-2"><Truck className="w-4 h-4 text-emerald-600 shrink-0" /> {shipInfo}</p>
            <Link to="/contatta-il-distributore" className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium" data-testid="product-contact-link"><MessageCircle className="w-4 h-4" /> Hai domande su questo prodotto? Contatta il distributore</Link>
          </div>

          {!soldOut && (
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <div className="flex items-center rounded-full border border-slate-200">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-slate-600 hover:text-primary" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="p-3 text-slate-600 hover:text-primary" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <Button size="lg" className="rounded-full flex-1 py-6" onClick={() => addItem(product, qty)} data-testid="detail-add-to-cart"><ShoppingCart className="w-5 h-5 mr-2" /> Aggiungi al carrello</Button>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16" data-testid="related-products">
          <h2 className="text-lg md:text-lg font-bold font-display text-slate-900">Ti potrebbero servire</h2>
          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}

      {!soldOut && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur px-4 py-3 flex items-center gap-3" data-testid="mobile-sticky-cart">
          <div className="flex flex-col leading-tight shrink-0">
            <span className="text-[11px] text-slate-500">Prezzo</span>
            <span className="text-lg font-extrabold text-slate-900">{formatPrice(product.price)}</span>
          </div>
          <Button size="lg" className="flex-1 rounded-full py-6" onClick={() => addItem(product, qty)} data-testid="mobile-add-to-cart"><ShoppingCart className="w-5 h-5 mr-2" /> Aggiungi al carrello</Button>
        </div>
      )}
    </div>
  );
}
